import React from 'react';
import { FaTruck, FaWeight, FaRoute, FaCalendarAlt, FaUser, FaBuilding } from 'react-icons/fa';

const StatCard = ({ icon: Icon, label, value, sub, color }) => {
  const colorMap = {
    teal: 'bg-primary-50 text-primary-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    violet: 'bg-violet-50 text-violet-600',
    slate: 'bg-slate-100 text-slate-600',
  };

  return (
    <div className="bg-surface rounded-xl border border-slate-200 shadow-sm p-3 lg:p-4 flex items-start gap-2 lg:gap-3 hover:shadow-md transition-shadow overflow-hidden">
      <div
        className={`flex-shrink-0 w-8 h-8 lg:w-10 lg:h-10 rounded-lg flex items-center justify-center ${colorMap[color]}`}
      >
        <Icon className="w-4 h-4 lg:w-5 lg:h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] lg:text-xs font-medium text-slate-500 uppercase tracking-wider leading-tight">
          {label}
        </p>
        <p
          className="text-sm lg:text-lg font-bold text-slate-900 mt-0.5 truncate"
          title={String(value)}
        >
          {value}
        </p>
        {sub && (
          <p className="text-[10px] lg:text-xs text-slate-400 mt-0.5 truncate" title={sub}>
            {sub}
          </p>
        )}
      </div>
    </div>
  );
};

const formatTonnage = (t) => {
  if (t >= 1000000) return `${(t / 1000000).toFixed(1)}M t`;
  if (t >= 1000) return `${(t / 1000).toFixed(1)}K t`;
  return `${t.toFixed(1)} t`;
};

const StatsCards = ({ stats }) => {
  if (!stats) return null;

  const cards = [
    {
      icon: FaTruck,
      label: 'Dispatch Trips',
      value: (stats.dispatchCount || 0).toLocaleString(),
      sub: `${(stats.receiptCount || 0).toLocaleString()} receipts`,
      color: 'teal',
    },
    {
      icon: FaWeight,
      label: 'Total Tonnage',
      value: formatTonnage(stats.totalDispatchTonnage || 0),
      sub: `Dispatched weight`,
      color: 'emerald',
    },
    {
      icon: FaRoute,
      label: 'Unique Routes',
      value: stats.uniqueRoutes || 0,
      sub: `${stats.uniqueDispatchLocations || 0} disp, ${stats.uniqueReceiptLocations || 0} rec`,
      color: 'blue',
    },
    {
      icon: FaCalendarAlt,
      label: 'Active Days',
      value: stats.activeDays || 0,
      sub:
        stats.firstTicket && stats.lastTicket
          ? `${new Date(stats.firstTicket).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' })} - ${new Date(stats.lastTicket).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric', year: 'numeric' })}`
          : null,
      color: 'amber',
    },
    {
      icon: FaUser,
      label: 'Primary Driver',
      value: stats.primaryDriver || 'N/A',
      sub: stats.primaryProduct || null,
      color: 'violet',
    },
    {
      icon: FaBuilding,
      label: 'Primary Haulier',
      value: stats.primaryHaulier || 'N/A',
      color: 'slate',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2 lg:gap-3">
      {cards.map((card, i) => (
        <StatCard key={i} {...card} />
      ))}
    </div>
  );
};

export default StatsCards;
